import React, { ReactNode, useCallback, useEffect, useState } from "react";

import { faInfoCircle } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { bem } from "@react-md/utils";
// eslint-disable-next-line no-restricted-imports
import { PopconfirmProps as AntdPopconfirmProps } from "antd/es/popconfirm";
import Popover from "antd/es/popover";
import cn from "classnames";

import { isNodeEmpty } from "@mapmycustomers/shared/util/react";

import Button, { ButtonProps } from "../Button";
import { useConfigProvider } from "../ConfigProvider";

const block = bem("mmc-popconfirm");

export interface PopconfirmProps
  extends Omit<AntdPopconfirmProps, "cancelButtonProps" | "okButtonProps" | "title"> {
  cancelButtonProps?: Omit<ButtonProps, "danger" | "onClick" | "size" | "type">;
  children?: ReactNode;
  danger?: boolean;
  description: ReactNode;
  okButtonProps?: Omit<ButtonProps, "danger" | "onClick" | "size" | "type">;
  title?: ReactNode;
}

const Popconfirm: React.FC<PopconfirmProps> = ({
  cancelButtonProps,
  cancelText,
  children,
  danger,
  description,
  disabled,
  icon,
  okButtonProps,
  okText,
  onCancel,
  onConfirm,
  open,
  overlayClassName,
  title,
  trigger = ["click"],
  ...antDPopconfirmProps
}) => {
  const configProvider = useConfigProvider();
  const [isOpen, setOpen] = useState(open);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  const handleCancel = useCallback(
    (e?: React.MouseEvent<HTMLElement, MouseEvent>) => {
      onCancel?.(e);
      handleClose();
    },
    [handleClose, onCancel]
  );

  const handleConfirm = useCallback(
    (e?: React.MouseEvent<HTMLElement, MouseEvent>) => {
      onConfirm?.(e);
      handleClose();
    },
    [handleClose, onConfirm]
  );

  useEffect(() => {
    setOpen(open);
  }, [open]);

  const renderIcon =
    icon !== null ? (
      <div className={block("icon")}>
        {icon ?? <FontAwesomeIcon className={block("defaultIcon")} icon={faInfoCircle} />}
      </div>
    ) : (
      icon
    );

  const renderDescription = <div className={block("descriptionText")}>{description}</div>;

  const renderTitle = title ? (
    <div className={block("message")}>
      {title === true ? configProvider.formatMessage("ui.popconfirm.title") : title}
    </div>
  ) : (
    renderDescription
  );

  const footer = (
    <div className={block("footer")}>
      <Button {...cancelButtonProps} onClick={handleCancel} size="small">
        {cancelText ?? configProvider.formatMessage("ui.popconfirm.footer.cancelText")}
      </Button>
      <Button
        {...okButtonProps}
        danger={danger}
        onClick={handleConfirm}
        size="small"
        type="primary"
      >
        {okText ?? configProvider.formatMessage("ui.popconfirm.footer.okText")}
      </Button>
    </div>
  );

  const handleOpenChange = useCallback((open: boolean) => {
    setOpen(open);
  }, []);

  return (
    <Popover
      content={
        isNodeEmpty(description) ? null : (
          <div className={block("content")}>
            <div className={block("innerContent")}>
              <div className={block("title")}>
                {renderIcon}
                {renderTitle}
              </div>
              {title && (
                <div
                  className={block("description", {
                    hasIcon: icon !== null,
                  })}
                >
                  {renderDescription}
                </div>
              )}
            </div>
            {footer}
          </div>
        )
      }
      onOpenChange={handleOpenChange}
      open={isOpen}
      overlayClassName={cn(block({ danger }), overlayClassName)}
      trigger={disabled ? [] : trigger}
      {...antDPopconfirmProps}
    >
      {children}
    </Popover>
  );
};

export default Popconfirm;
